/**
 * ============================================================================
 *  SCHEMA — Dynamic Form / Dossier config (Zod)
 * ============================================================================
 *
 * Tách rời khỏi `./constants.ts` và `./types.ts`. File này chỉ tập trung vào
 * Zod schema và các helper parse/serialize JSON liên quan tới schema.
 */

import { ComboboxOption } from '@/components/custom-select';
import type { IHrmThongTinPhepRecord } from '@/hooks/query/use-query-kiem-tra-thong-tin-phep';
import type { ITienDoDaoTaoCuaSinhVienResponse } from '@/hooks/query/use-query-tien-do-dao-tao';
import type { IHrmThongTinGiangVienResponse } from '@/server/api/v1/employee/hrm-giang-vien';
import { LogicalOperator, OperatorType } from '@/server/base/base-trpc-service';
import type { UISStudentInfoResponse } from '@/server/api/v1/uis-student/type';
import { z } from 'zod';
import {
    APP_BUSINESS_DICTIONARY_CODES,
    ConditionOperator,
    QuestionType,
} from './constants';
import type { DossierSchemaConfig, FormSchemaConfig } from './types';

/* -------------------------------------------------------------------------- */
/*                          Zod schema cho form động                            */
/* -------------------------------------------------------------------------- */

/**
 * Schema chính cho cấu hình form động của 1 thủ tục.
 * Tách thành function-return để cho phép truyền i18n hoặc deps khi cần.
 */
export const DynamicFormConfigSchema = () => {
    const option = z.object({
        key: z.string(),
        title: z.string(),
        value: z.string(),
        /**
         * Từ khóa mail merge RIÊNG của option — admin chọn từ combobox
         * `MailMergeKeyInput` ở `OptionsListEditor` (hoặc gõ key mới để tự thêm
         * vào danh sách trung tâm `availableMailMergeKeys`).
         *
         * Khi câu hỏi `radio`/`checkbox` được tick, engine
         * `mapExtendedInfoToRecord` push record:
         *   { Key: opt.mail_merge_key, Value: 'true' | 'false' }
         *
         * Optional + fallback `opt.value || opt.title` để **tương thích ngược**
         * với data đã lưu (trước đây quy ước "value = mail merge key" — vẫn chạy
         * bình thường nếu option chưa khai báo `mail_merge_key`).
         */
        mail_merge_key: z.string().optional(),
    });

    const question = z
        .object({
            /** Unique key của câu hỏi */
            key: z.string(),
            /** Tên hiển thị câu hỏi */
            title: z.string().default(''),
            /** Mô tả/giải thích cho người điền */
            description: z.string().optional(),
            /**
             * Loại câu hỏi — mặc định "Trả lời ngắn" (text).
             * Không cho phép undefined: khi tạo mới câu hỏi mà admin chưa chọn loại,
             * schema sẽ tự fill về `QuestionType.text` thay vì để rỗng.
             */
            type: z.nativeEnum(QuestionType).default(QuestionType.text),
            /** Danh sách option cho select/radio/checkbox */
            options: z.array(option).default([]),

            /**
             * Key của trường đục lỗ (mail merge).
             * Trước đây là union với enum `MAIL_MERGE_KEYs` (đã gỡ bỏ) — nay chỉ còn
             * `string` tự do, admin nhập/chọn key tuỳ ý.
             */
            mail_merge_key: z.string().optional(),

            /** Các mail merge key liên quan */
            related_mail_merge_keys: z.array(z.string()).optional(),

            /** Giá trị câu trả lời */
            value: z.string().optional(),

            /** Key để gọi API (cho directories) */
            api_key: z.string().optional(),
            /** Params kèm theo khi gọi API */
            api_params_keys: z.array(z.string()).optional(),
            /**
             * Cho phép chọn NHIỀU giá trị — chỉ áp dụng cho type=directories.
             *
             * Khi bật: value runtime lưu dạng `JSON.stringify(string[])` (giống hệt
             * quy ước của `checkbox`), render bằng `CustomSelect.Multiple` thay vì
             * `CustomSelect.Single`. Ghi dict mail-merge dùng chung logic join theo
             * `checkbox_separator` với `checkbox`.
             *
             * Không áp dụng cho dictionary có component `Field` riêng trong registry
             * (vd `UIS_ThoiGianNghiHoc`) — các Field này tự quyết định UI chọn 1/nhiều.
             */
            is_multiple: z.boolean().optional(),

            /** Từ khoá thống kê */
            statistical_key: z.string().optional(),

            /** Có hiển thị description không */
            isShowDescription: z.boolean().default(false),
            /** Có bắt buộc nhập không */
            isRequired: z.boolean().optional(),

            /**
             * Đối với select/radio/checkbox: dùng title làm giá trị mail merge.
             */
            isUseTitleFieldAsMailMergeValue: z.boolean().optional(),

            /**
             * Cờ "Giá trị và nhãn riêng" — chỉ áp dụng cho select/radio/checkbox.
             *
             * - false (mặc định): label = value, ô nhập giá trị bị disable trong cấu hình.
             *   Runtime fallback `value || title` đảm bảo dữ liệu trả về vẫn là chính nhãn.
             * - true: admin nhập value RIÊNG cho từng option (khác label).
             *
             * Khi tắt cờ này, value đã nhập KHÔNG bị xoá — chỉ disable ô nhập.
             */
            isUseSeparateValueLabel: z.boolean().optional(),

            /**
             * Ký tự nối các giá trị đã chọn của câu hỏi `checkbox` để tạo chuỗi
             * gán cho mail merge của câu hỏi cha (`mail_merge_key`).
             *
             * Ví dụ: 3 option được tick có value ['A','B','C'], `checkbox_separator = ';'`
             *   → record cha: `{Key: parent_mm_key, Value: 'A;B;C'}`.
             *
             * Mặc định undefined → fallback ',' trong logic mapExtendedInfoToRecord.
             * Chỉ enum một số ký tự an toàn để tránh vỡ template Word khi nhập tự do.
             */
            checkbox_separator: z
                .enum([',', ';', '/', '.', '|', ' '])
                .optional(),

            /**
             * "Mô tả hiển thị có điều kiện" — thay thế hoàn toàn phần mô tả tĩnh cũ.
             *
             * Cấu trúc:
             * - `mode`: 'priority' = chỉ render mẫu ĐẦU TIÊN thỏa điều kiện;
             *           'all' = render TẤT CẢ mẫu thỏa (nối lại).
             * - `items[]`: mỗi mẫu gồm:
             *     + `template`   : chuỗi text có placeholder `{{Key}}` (HTML inline OK).
             *     + `conditions[]`: danh sách điều kiện (AND) đối chiếu giá trị từ khoá
             *       đục lỗ. Rỗng = luôn thỏa. Mỗi điều kiện: { key, operator, value? }.
             *
             * Runtime: engine `renderConditionalDescriptions` (placeholder.ts) đánh giá
             * điều kiện theo context (mail merge dict + builtin) rồi render placeholder.
             */
            conditionalDescriptions: z
                .object({
                    mode: z.enum(['priority', 'all']).default('priority'),
                    items: z
                        .array(
                            z.object({
                                template: z.string().default(''),
                                conditions: z
                                    .array(
                                        z.object({
                                            /** Tên từ khoá đục lỗ cần đối chiếu */
                                            key: z.string().default(''),
                                            /** Toán tử so sánh */
                                            operator: z
                                                .nativeEnum(ConditionOperator)
                                                .default(
                                                    ConditionOperator.HasValue
                                                ),
                                            /** Giá trị so sánh (không cần cho hasValue/noValue) */
                                            value: z.string().optional(),
                                            /**
                                             * Tùy chọn riêng cho toán tử "Bằng"/"Khác":
                                             * - `caseInsensitive`  : so sánh KHÔNG phân biệt hoa/thường.
                                             * - `ignorePunctuation`: bỏ qua dấu câu (.,;:!? ...) trước khi so sánh.
                                             * Mặc định undefined = so sánh tuyệt đối như cũ → không phá vỡ dữ liệu cũ.
                                             */
                                            caseInsensitive: z
                                                .boolean()
                                                .optional(),
                                            ignorePunctuation: z
                                                .boolean()
                                                .optional(),
                                        })
                                    )
                                    .default([]),
                            })
                        )
                        .default([]),
                })
                .optional(),

            /**
             * Cấu hình UI cho các loại câu hỏi cơ bản (text/textarea/number/date).
             *
             * - `minLength`/`maxLength`: ràng buộc độ dài chuỗi cho text/textarea
             *   (runtime validate khi sinh viên nhập). `minLength` mới thêm vào
             *   để admin có thể yêu cầu câu trả lời tối thiểu N ký tự.
             * - `withTime`: chỉ áp dụng cho type=date — bật để cho phép chọn thêm
             *   giờ/phút (granularity='minute'), tắt = chỉ chọn ngày (granularity='day').
             *   Giá trị `value` của câu hỏi luôn lưu dạng ISO string (vd
             *   "2026-08-21T01:31:25.128Z") bất kể có bật giờ hay không.
             */
            uiConfig: z
                .object({
                    placeholder: z.string().optional(),
                    rows: z.number().min(1).optional(),
                    minLength: z.number().min(1).optional(),
                    maxLength: z.number().min(1).optional(),
                    min: z.number().optional(),
                    max: z.number().optional(),
                    step: z.number().min(0).optional(),
                    withTime: z.boolean().optional(),
                })
                .optional(),

            /**
             * Cấu hình riêng cho loại arrayObject.
             */
            arrayObjectConfig: z
                .object({
                    /** Số nhóm tối thiểu (mặc định 1) */
                    minItems: z.number().min(1).optional(),
                    /** Số nhóm tối đa (undefined = không giới hạn) */
                    maxItems: z.number().min(1).optional(),
                    /**
                     * Cấu trúc các field con. Không hỗ trợ arrayObject lồng nhau.
                     */
                    fields: z
                        .array(
                            z.object({
                                key: z.string(),
                                label: z.string().default(''),
                                type: z.string().default('text'),
                                isRequired: z.boolean().default(false),
                                options: z.array(option).optional(),
                                placeholder: z.string().optional(),
                                mail_merge_key: z.string().optional(),
                                /**
                                 * Mã từ điển dữ liệu — chỉ có tác dụng với type=directories.
                                 */
                                api_key: z.string().optional(),
                                /**
                                 * Cho phép chọn nhiều — chỉ có tác dụng với type=select/directories
                                 * (đồng bộ `is_multiple` ở câu hỏi cấp 1). Giá trị lưu ở `ItemRecord`
                                 * chuyển sang JSON `{label,value}[]` khi bật (xem `RenderArrayObject`).
                                 */
                                is_multiple: z.boolean().optional(),
                            })
                        )
                        .default([]),
                })
                .optional(),
        })
        .refine(
            (data) => {
                if (data.isRequired) {
                    return !!data.value;
                }
                return true;
            },
            {
                message: 'Trường này là bắt buộc',
                path: ['value'],
            }
        );

    return z.object({
        /** Danh sách câu hỏi form động */
        questions: z.array(question).default([]),
        /** Lưu lại để thống kê */
        objStatisticalAnswer: z.record(z.any()).optional().nullable(),
        /** Danh sách cấu hình mẫu file quyết định */
        objPrintDecisions: z.array(z.object({
            id: z.string(),
            name: z.string().optional(),
            khoaTuyenSinhFrom: z.string(),
            khoaTuyenSinhTo: z.string(),
            hinhThucDaoTao: z.string().optional(),
            declarationFormCodes: z.array(z.string()),
        })).default([]),
        /** Danh sách các trường có thể dùng mail_merge */
        availableMailMergeKeys: z.array(z.string()).optional(),
        /** Danh sách từ khoá đục lỗ thủ công */
        mailMergeManualKeys: z.array(z.string()).optional(),
        /** Trạng thái SV được phép */
        trangThaiSinhVien: z.array(z.string()).optional(),
        /** ID của chương trình đào tạo cần dùng (nếu có) */
        useStudyProgramId: z.string().optional().nullable(),
        /** Thời gian xử lý tính từ học kỳ tiếp theo */
        isHandleFromNextTerm: z.boolean().optional(),
        /** Text thông báo sau khi nộp hồ sơ thành công */
        text_SubmitDossiertSuccessfully: z.string().optional(),
        /** Regex bắt theo người tiếp nhận online */
        onlineReceiverRules: z.array(z.object({
            employeeID: z.string(),
            regexs: z.string(),
        })).optional(),
        /** Danh sách đối tượng đã pass online */
        onlineReceiversPassed: z.array(z.string()).optional(),
        /** Tiến độ đào tạo hiện tại của SV khi nộp hồ sơ */
        currentTraningProcess: z
            .object({
                yearStudy: z.string().optional(),
                termID: z.string().optional(),
            })
            .optional(),
        /**
         * Snapshot thông tin sinh viên chốt tại thời điểm CÁN BỘ TIẾP NHẬN hồ sơ.
         *
         * - `vanBangInfo`: thông tin SV theo `studyProgramId` đã resolve
         *   (gọi `uis_thong_tin_sinh_vien_theo_chuong_trinh_dao_tao`). Lưu
         *   dạng record động (UISStudentInfoResponse) — print cast lại khi merge.
         * - `tienDoDaoTao`: dữ liệu gốc tiến độ đào tạo (kỳ đã học / còn lại,
         *   năm tuyển sinh / năm hiện tại) — print time chạy `runSnapshotMaps`
         *   để tính ra records. Đặt CÙNG `studentSnapshot` vì cùng là thông
         *   tin sinh viên, snapshot cùng thời điểm tiếp nhận.
         * - `studyProgramId`: chương trình đào tạo đã dùng để fetch vanBangInfo.
         * - `capturedAt`: thời điểm chốt (ISO) để truy vết.
         *
         * Mục đích: khi in quyết định, ưu tiên đọc snapshot này thay vì fetch live
         * (chỉ fetch live khi snapshot THIẾU key mà template docx cần).
         */
        studentSnapshot: z
            .object({
                // Dùng `z.custom<T>()` thay vì `z.record(z.any())` để
                // type của field này khớp đúng `UISStudentInfoResponse`
                // ở caller — không cần cast lại lúc đọc snapshot. Runtime
                // KHÔNG validate shape (giống record cũ); guard ở print
                // pipeline đảm bảo crash-safe khi data hỏng.
                vanBangInfo: z.custom<UISStudentInfoResponse>().optional(),
                tienDoDaoTao: z
                    .custom<ITienDoDaoTaoCuaSinhVienResponse>()
                    .optional(),
                studyProgramId: z.string().optional(),
                capturedAt: z.string().optional(),
            })
            .optional(),
        /**
         * Snapshot thông tin CÁN BỘ (HRM) chốt tại thời điểm CÁN BỘ TIẾP NHẬN
         * hồ sơ — song song với `studentSnapshot` nhưng cho người nộp là cán bộ
         * (có `CivilServantCode`), dùng cho thủ tục kiểu "Xin nghỉ phép".
         *
         * - `thongTinGiangVien`: HRM_GetThongTinGiangVien (họ tên, đơn vị...).
         * - `thongTinPhep`: HRM_KiemTraThongTinPhep (tổng/đã nghỉ/còn lại).
         * - `capturedAt`: thời điểm chốt (ISO) để truy vết.
         *
         * Xem `custom-modal-print-decision/staff-snapshot.ts` (`buildStaffSnapshot`).
         */
        staffSnapshot: z
            .object({
                thongTinGiangVien: z
                    .custom<IHrmThongTinGiangVienResponse>()
                    .optional(),
                thongTinPhep: z.custom<IHrmThongTinPhepRecord>().optional(),
                capturedAt: z.string().optional(),
            })
            .optional(),
        /**
         * Thông tin số ngày xin nghỉ chốt tại thời điểm NGƯỜI NỘP bấm nộp hồ sơ
         * (thủ tục "Xin nghỉ phép" — HRM). Khác `staffSnapshot` (chốt lúc tiếp
         * nhận): số phép còn lại có thể đổi sau đó nên cần lưu đúng lúc người nộp
         * thấy & xác nhận. Ghi bởi `HrmLeaveBalanceWarning` qua `setValue`.
         *
         * - `soNgay`: số ngày đăng ký nghỉ (HRM_TinhSoNgayDangKyNghi).
         * - `soNgayPhepConLai`: số phép còn lại (HRM_KiemTraThongTinPhep).
         * - `capturedAt`: thời điểm chốt (ISO) để truy vết.
         */
        leaveRequestInfo: z
            .object({
                soNgay: z.number().optional(),
                soNgayPhepConLai: z.number().optional(),
                capturedAt: z.string().optional(),
                /**Thông tin mở rộng của thủ tục in bảng điểm */
                email: z.string().optional(),
                maChuongTrinh: z.string().optional(),
                maSinhVien: z.string().optional(),
                maYeuCau: z.string().optional(),
                tenYeuCau: z.string().optional(),
                SoDienThoai: z.string().optional(),
            })
            .optional(),
        /**
         * Snapshot DỮ LIỆU GỐC (raw) NGOÀI phạm vi thông tin sinh viên —
         * dành riêng cho các nguồn dữ liệu non-SV (vd: cấu hình thủ tục,
         * tham số nghiệp vụ) cần snapshot khi tiếp nhận.
         *
         * Hiện tại để rỗng. `tienDoDaoTao` (thông tin SV) đã chuyển sang
         * `studentSnapshot.tienDoDaoTao` — KHÔNG đọc/ghi key này nữa.
         */
        snapshotData: z.object({}).passthrough().optional(),
        /** Các option cấu hình thêm */
        options: z
            .object({
                isShowTextRegisterStudyLeave: z.boolean().optional(),
                isNeedConfirmByAcademicAdvisor: z.boolean().optional(),
            })
            .default({}),
    });
};

/* -------------------------------------------------------------------------- */
/*                       Zod schema cho cấu hình dossier                        */
/* -------------------------------------------------------------------------- */

/** 1 điều kiện filter trong cấu hình dossier (extendedInfoJson). */
export const DossierBusinessFilterSchema = z.object({
    key: z.string(),
    value: z.any(),
    logicalOperator: z.nativeEnum(LogicalOperator),
    operatorType: z.nativeEnum(OperatorType),
});

export const DynamicDossierConfigSchema = () => {
    return z.object({
        filterConditions: z.array(DossierBusinessFilterSchema).optional(),
        /** Cho phép tải nhiều giấy tờ cùng lúc */
        isAllowUploadMultiple: z.boolean().optional(),
        /** Các định dạng file được phép tải lên */
        allowExtensions: z.array(z.string()).optional(),
        /**
         * Chỉ sử dụng cho các file trước đó.
         * @deprecated
         */
        conditions: z.any().optional(),
    });
};

/* -------------------------------------------------------------------------- */
/*                Danh sách KEY được phép lưu vào extendedInfoJson              */
/* -------------------------------------------------------------------------- */

type KeyFormSchemaConfig = keyof FormSchemaConfig;

/** Các key được phép lưu trong extendedInfoJson của THỦ TỤC. */
export const key_can_save_procedure_dynamic_form: KeyFormSchemaConfig[] = [
    'questions',
    'objStatisticalAnswer',
    'objPrintDecisions',
    'availableMailMergeKeys',
    'mailMergeManualKeys',
    'trangThaiSinhVien',
    'text_SubmitDossiertSuccessfully',
    'onlineReceiverRules',
    'onlineReceiversPassed',
    'options',
    'currentTraningProcess',
];

/** Các key được phép lưu trong extendedInfoJson của HỒ SƠ. */
export const key_can_save_dossier_dynamic_form: KeyFormSchemaConfig[] = [
    'questions',
    'objPrintDecisions',
    'availableMailMergeKeys',
    'mailMergeManualKeys',
    'trangThaiSinhVien',
    'onlineReceiverRules',
    'onlineReceiversPassed',
    'options',
    'currentTraningProcess',
    // Snapshot thông tin SV chốt lúc cán bộ tiếp nhận (xem [[student-snapshot]]).
    'studentSnapshot',
    // Snapshot thông tin CÁN BỘ chốt lúc tiếp nhận (xem [[staff-snapshot]]).
    'staffSnapshot',
    // Số ngày xin nghỉ / số phép còn lại chốt lúc NGƯỜI NỘP bấm nộp hồ sơ.
    'leaveRequestInfo',
    // Snapshot DỮ LIỆU GỐC cho pipeline in (xem `runSnapshotMaps`).
    'snapshotData',
    // TODO: 'objStatisticalAnswer' — không dùng nữa.
];

/* -------------------------------------------------------------------------- */
/*               Helpers parse / serialize extendedInfoJson string              */
/* -------------------------------------------------------------------------- */

/**
 * Parse chuỗi `ExtendedInfoJson` của THỦ TỤC sang `FormSchemaConfig`.
 * Trả về object mặc định nếu input rỗng/không hợp lệ.
 */
export function parseDynamicFormConfigData(extendedInfo?: string) {
    let data: FormSchemaConfig = {
        options: {},
        questions: [],
        objPrintDecisions: [],
        availableMailMergeKeys: [],
        mailMergeManualKeys: [],
        trangThaiSinhVien: [],
        useStudyProgramId: null,
    };

    if (extendedInfo) {
        try {
            const extendedInfoJSON = JSON.parse(extendedInfo);
            if (
                typeof extendedInfoJSON === 'object' &&
                Object.keys(extendedInfoJSON).length > 0 &&
                (extendedInfoJSON satisfies FormSchemaConfig)
            ) {
                data = { ...extendedInfoJSON } as any;
            }
        } catch (error) {
            console.log("Can't parse extendedInfoJSON", error);
        }
    }

    return data;
}

/**
 * Parse chuỗi extendedInfo của THÀNH PHẦN HỒ SƠ sang `DossierSchemaConfig`.
 */
export function parseDynamicDossierCmpConfig(extendedInfo?: string) {
    let data: DossierSchemaConfig = {
        filterConditions: [],
    };

    if (extendedInfo) {
        try {
            const parseData = JSON.parse(extendedInfo);
            if (
                typeof parseData === 'object' &&
                Object.keys(parseData).length > 0
            ) {
                data = { ...parseData } as any;
            }
        } catch (error) {
            console.error(' ExtendedInfoJson không hợp lệ', error);
        }
    }

    return data;
}

/* -------------------------------------------------------------------------- */
/*                      Mapping kết quả API → ComboboxOption                    */
/* -------------------------------------------------------------------------- */

/**
 * Map kết quả gọi API (theo mã DICTIONARY_CODES) sang `ComboboxOption[]`.
 * Hàm này chứa logic phụ thuộc shape dữ liệu của từng từ điển nên đặt cạnh
 * danh sách mã từ điển ở `./constants.ts`.
 */
export function mapDirectoriesToOptions(key: string, data: any[]) {
    const options: ComboboxOption<any>[] = [];
    if (data.length) {
        for (const x of data) {
            let obj: ComboboxOption<any> | undefined = undefined;
            switch (key) {
                case APP_BUSINESS_DICTIONARY_CODES.uis_hinh_thuc_dao_tao_tai_truong:
                    obj = {
                        value: x?.KiHieu,
                        label: `${x?.HinhThucDaoTao} (Kí hiệu: ${x?.KiHieu})`,
                        _origin: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_loai_hinh_dao_tao:
                    obj = {
                        label: x.StudyTypeName,
                        value: x.StudyTypeID,
                        _origin: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_chuong_trinh_dao_tao_theo_khoa_moi_nhat:
                    obj = {
                        label: x.StudyProgramName,
                        value: x.StudyProgramID,
                        _origin: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_lop_theo_chuong_trinh_dao_tao:
                    obj = {
                        label: x.ClassStudentID,
                        value: x.ClassStudentID,
                        _origin: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_diem_ren_luyen_sinh_vien_theo_hoc_ky:
                case APP_BUSINESS_DICTIONARY_CODES.uis_danh_sach_hoc_ky_co_diem_ren_luyen_cua_sinh_vien:
                    obj = {
                        label: `Học kỳ ${x.Semester} năm học ${x.YearStudy}`,
                        value: x.KeyData,
                        _origin: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_lay_thong_tin_dich_vu_bang_diem:
                    const label = [{id: 'BangDiemCT', title: 'Bảng điểm chi tiết'}, {id: 'BangDiemTB', title: 'Bảng điểm trung bình'}].find(y => y.id === x.MaYeuCau)?.title
                    obj = {
                        label: label,
                        value: x.MaYeuCau,
                        _origin: {MaSinhVien: x.MaSinhVien, MaChuongTrinh: x.MaChuongTrinh ,MaYeuCau: x.MaYeuCau, Email: x.Email, SoDienThoai: x.SoDienThoai, label: label},
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_lay_danh_sach_nam_hoc_dich_vu:
                    obj = {
                        label: x,
                        value: x,
                    };
                    break;
                case APP_BUSINESS_DICTIONARY_CODES.uis_get_all_mon_hoc_dich_vu:
                    obj = {
                        label: `${x.TenMonHoc} (${x.SoTC} TC)`,
                        value: x.MaMonHoc,
                        _origin: x,
                    };
                    break;
            }

            if (obj) options.push(obj);
        }
    } else {

    }

    return options;
}

/* -------------------------------------------------------------------------- */
/*         Giá trị câu hỏi "danh sách chọn" — { label, value }                  */
/* -------------------------------------------------------------------------- */

/**
 * Quy ước LƯU CHUNG cho MỌI câu hỏi (top-level `select`/`radio`/`directories`
 * KHÔNG `is_multiple`, VÀ field con cùng loại trong `arrayObject`/`advanced`):
 * value lưu JSON `{ label, value }` thay vì chỉ lưu chuỗi `value` thô.
 * - `value` : id lựa chọn (ổn định, dùng để so khớp lại option).
 * - `label` : tiêu đề hiển thị — lưu kèm để mail-merge / in quyết định / tóm
 *   tắt / thống kê không phải tra cứu lại `options[]` (options của
 *   `directories` fetch ĐỘNG từ API nên bắt buộc phải lưu kèm label).
 *
 * `checkbox` và `directories` có `is_multiple = true` dùng biến thể MẢNG —
 * xem `parseFieldChoiceValueList`/`serializeFieldChoiceValueList` bên dưới.
 */
export type FieldChoiceValue = { label: string; value: string };

/**
 * Các `QuestionType` lưu value dạng ĐƠN `{ label, value }` (không phải mảng).
 * `directories` chỉ khớp quy ước này khi `is_multiple` KHÔNG bật — kiểm tra
 * thêm `question.is_multiple` tại nơi gọi.
 */
export const FIELD_CHOICE_TYPES: ReadonlySet<string> = new Set<string>([
    QuestionType.select,
    QuestionType.radio,
    QuestionType.directories,
]);

/**
 * Parse value đã lưu của 1 câu hỏi/field "danh sách chọn" (dạng ĐƠN).
 *
 * Tương thích ngược: data cũ lưu chuỗi thô (id hoặc title) — coi chuỗi đó vừa
 * là `value` vừa là `label`.
 */
export function parseFieldChoiceValue(raw?: string): FieldChoiceValue {
    if (!raw) return { label: '', value: '' };
    try {
        const parsed = JSON.parse(raw);
        if (
            parsed &&
            typeof parsed === 'object' &&
            !Array.isArray(parsed) &&
            typeof parsed.value === 'string'
        ) {
            return {
                value: parsed.value,
                label:
                    typeof parsed.label === 'string'
                        ? parsed.label
                        : parsed.value,
            };
        }
    } catch {
        // chuỗi thô — rơi xuống fallback bên dưới
    }
    return { label: raw, value: raw };
}

/** Serialize 1 lựa chọn "danh sách chọn" (dạng ĐƠN) về JSON `{ label, value }`. */
export function serializeFieldChoiceValue(
    value: string,
    label?: string
): string {
    return JSON.stringify({
        value,
        label: typeof label === 'string' && label ? label : value,
    });
}

/**
 * Lấy nhãn hiển thị từ value đã lưu (dạng ĐƠN) — dùng cho mail-merge / tóm
 * tắt / thống kê. JSON `{ label, value }` → `label`; chuỗi thô cũ → giữ nguyên.
 */
export function getFieldChoiceLabel(raw?: string): string {
    return parseFieldChoiceValue(raw).label;
}

/**
 * Parse value đã lưu của câu hỏi "danh sách chọn" dạng MẢNG (`checkbox`,
 * `directories` có `is_multiple = true`) — JSON `{ label, value }[]`.
 *
 * Tương thích ngược: data cũ lưu `JSON.stringify(string[])` (chỉ id/title,
 * không có label) — mỗi phần tử string được coi vừa là `value` vừa là `label`.
 */
export function parseFieldChoiceValueList(raw?: string): FieldChoiceValue[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((x) => {
            if (
                x &&
                typeof x === 'object' &&
                !Array.isArray(x) &&
                typeof x.value === 'string'
            ) {
                return {
                    value: x.value,
                    label: typeof x.label === 'string' ? x.label : x.value,
                };
            }
            const v = String(x ?? '');
            return { value: v, label: v };
        });
    } catch {
        return [];
    }
}

/** Serialize danh sách lựa chọn "danh sách chọn" (dạng MẢNG) về JSON `{ label, value }[]`. */
export function serializeFieldChoiceValueList(items: FieldChoiceValue[]): string {
    return JSON.stringify(
        items.map((x) => ({
            value: x.value,
            label: x.label || x.value,
        }))
    );
}

/**
 * Nối nhãn hiển thị của value đã lưu (dạng MẢNG) bằng `separator` — dùng cho
 * mail-merge / tóm tắt / thống kê. Tương thích ngược qua `parseFieldChoiceValueList`.
 */
export function getFieldChoiceLabelList(raw?: string, separator = ', '): string {
    return parseFieldChoiceValueList(raw)
        .map((x) => x.label)
        .filter(Boolean)
        .join(separator);
}
